import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBalanceScale, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const WarehouseInventoryAdjustForm = ({ 
  isOpen, 
  onClose, 
  onCancel, 
  onSubmit, 
  inventory = null, 
  loading = false
}) => {
  const { t } = useTranslation();
  const strings = t('warehouseInventory.adjustForm', { returnObjects: true });
  const fields = strings.fields;
  const REQUIRED = t('common.form.requiredIndicator');

  // Normalize close callback
  const closeHandler = useCallback(() => {
    if (typeof onClose === 'function') return onClose();
    if (typeof onCancel === 'function') return onCancel();
    return undefined;
  }, [onClose, onCancel]);

  const [form, setForm] = useState({
    quantity: '',
    reason: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setForm({
        quantity: '',
        reason: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    
    // Apply input filters for numeric fields
    if (name === 'quantity') {
      // Allow typing minus sign at the beginning and numeric characters
      // Don't filter the input too aggressively to allow negative numbers
      v = value; // Let the user type freely for now
    }
    
    setForm(prev => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const v = {};
    
    // Quantity is required and must be a valid number (allow negative and positive, but not zero)
    if (!form.quantity || !String(form.quantity).trim()) {
      v.quantity = fields.quantity.errors.required;
    } else if (isNaN(Number(form.quantity)) || Number(form.quantity) === 0) {
      v.quantity = fields.quantity.errors.invalid;
    }
    
    // Reason is required
    if (!form.reason || !String(form.reason).trim()) {
      v.reason = fields.reason.errors.required;
    }
    
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        quantity: parseFloat(form.quantity),
        reason: form.reason.trim(),
      };

      // Close only on success — keep the entered adjustment on a backend rejection.
      const res = await onSubmit(payload);
      if (!res || res.success) closeHandler();
    } catch (submitError) {
      console.error('Error submitting adjust stock form:', submitError);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const scrollClass = 'warehouse-inventory-adjust-form-scroll';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-lg relative bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {strings.title}
          </h2>
          <button 
            onClick={closeHandler} 
            className="p-2 rounded-lg hover:opacity-75 transition-opacity" 
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {inventory && (
          <div className="px-6 pt-4 pb-2 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {strings.labels.adjustingFor}
                </div>
            <div className="mt-1">
              <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {inventory?.product_details?.alias_name || inventory?.product_name || strings.labels.unknownProduct}
              </div>
              <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.labels.atWarehousePrefix} {inventory?.warehouse_details?.name || inventory?.warehouse_name || strings.labels.unknownWarehouse}
              </div>
              <div className="text-sm mt-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.labels.currentStock} <span className="font-medium">{inventory?.stock_quantity || 0}{inventory?.track_by_pieces ? ` ${t('pieces.inventory.unitPieces')}` : ''}</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {strings.buttons.loading}
                </p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Quantity field */}
            <div>
              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                {fields.quantity.label}{inventory?.track_by_pieces ? ` (${t('pieces.inventory.unitPieces')})` : ''} {REQUIRED}
              </label>
              <input 
                name="quantity" 
                value={form.quantity} 
                onChange={handleChange} 
                className="w-full px-4 py-3 rounded-xl border-2" 
                placeholder={fields.quantity.placeholder} 
                style={{ 
                  backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                  borderColor: errors.quantity ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }} 
              />
              {errors.quantity && (
                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.quantity}</div>
              )}
              <div className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {strings.labels.quantityHint}
              </div>
            </div>

            {/* Reason field */}
            <div>
              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                {fields.reason.label} {REQUIRED}
              </label>
              <textarea
                name="reason" 
                value={form.reason} 
                onChange={handleChange} 
                rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 resize-none" 
                placeholder={fields.reason.placeholder} 
                style={{ 
                  backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                  borderColor: errors.reason ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }} 
              />
              {errors.reason && (
                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.reason}</div>
              )}
            </div>

            <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <button 
                type="button"
                onClick={closeHandler}
                className="px-6 py-3 rounded-xl font-semibold" 
                style={{ 
                  backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, 
                  color: UI_COLORS.BUTTONS.SECONDARY.text 
                }}
              >
                {strings.buttons.cancel}
              </button>
              <button 
                type="submit" 
                disabled={loading || submitting} 
                className="px-6 py-3 rounded-xl font-semibold" 
                style={{ 
                  backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, 
                  color: UI_COLORS.BUTTONS.PRIMARY.text 
                }}
              >
                <FontAwesomeIcon icon={faBalanceScale} className="mr-2" /> 
                {strings.buttons.submit}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarehouseInventoryAdjustForm;